<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { AkButton, AkIcon } from '@ankorstore/design-system';
import VariantForm from '@bc/shared/components/add-to-cart/variant-form.vue';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import ProductSnippet from '@monolith/legacy/components/product/product-snippet-async.vue';
import type Product from '@monolith/legacy/types/product';
import type { Variant, PriceBreakdown } from '@monolith/legacy/types/product';
import { useStore } from '@core/composables/use-store';
import { useCart } from '@monolith/legacy/modules/cart-2/composables/use-cart';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';
import LegacyAddToCart from '@monolith/legacy/components/cart/add-to-cart.vue';
import { useFlyToCart } from '@bc/shared/composables/use-fly-to-cart';

const props = defineProps<{
  productId: number;
  priceBreakdown?: PriceBreakdown;
}>();

const emit = defineEmits<{
  addToCartVariant: [payload: unknown];
  updateItemCart: [payload: unknown];
}>();

const { t } = useI18n();
const store = useStore();
const { updateItemQuantity, ensureCart } = useCart();
const { triggerFly } = useFlyToCart();
const actionsRef = useTemplateRef<HTMLElement>('actionsRef');

const product = computed<Product>(() => store.getters.productById(props.productId));
const useSharedAddToCart = isEnabled(FeatureFlag.SharedAddToCart);

const selectedVariant = ref<Variant | undefined>();
const selectedQuantity = ref(0); // in units
const isLoading = ref(false);
const confirmation = ref(false);
let confirmationTimer: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
  if (!product.value?.refreshed) {
    store.dispatch('fetchProduct', { product_id: props.productId });
  }
});

const handleVariantSelected = (payload: { variant: Variant | undefined; quantity: number }) => {
  selectedVariant.value = payload.variant;
  selectedQuantity.value = payload.quantity;
};

const handleAddToCart = async () => {
  if (!selectedVariant.value?.uuid || isLoading.value) {return;}
  isLoading.value = true;
  try {
    await ensureCart();
    await updateItemQuantity(selectedVariant.value.uuid, selectedQuantity.value, true);
    if (actionsRef.value) {triggerFly(actionsRef.value);}
    confirmation.value = true;
    confirmationTimer = setTimeout(() => {
      confirmation.value = false;
      confirmationTimer = null;
    }, 2000);
    emit('addToCartVariant', {
      brand: product.value.brand,
      option: product.value.options?.find((o) => o.sku === selectedVariant.value?.sku),
      variant: selectedVariant.value,
      quantity: selectedQuantity.value,
    });
  } finally {
    isLoading.value = false;
  }
};

const handleLegacyAddToCart = (payload: unknown) => {
  emit('addToCartVariant', payload);
};

const handleLegacyUpdateItemCart = (payload: unknown) => {
  emit('updateItemCart', payload);
};

onUnmounted(() => {
  if (confirmationTimer) {clearTimeout(confirmationTimer);}
});
</script>

<template>
  <div
    v-if="product"
    class="popin-container"
  >
    <div class="product-block">
      <ProductSnippet
        class="product-snippet"
        :item="product"
        :with-quick-add-to-cart="false"
        :with-link-to-fp="true"
        :variant="selectedVariant"
        :with-new-badge="false"
      />
    </div>
    <template v-if="!useSharedAddToCart">
      <LegacyAddToCart
        v-if="product && product.refreshed"
        class="add-to-cart"
        :product="product"
        :price-breakdown="priceBreakdown"
        @add-to-cart="handleLegacyAddToCart"
        @update-item-cart="handleLegacyUpdateItemCart"
      />
      <LoaderIcon v-if="product && !product.refreshed" />
    </template>
    <template v-else>
      <div
        v-if="product.refreshed"
        ref="actionsRef"
        class="popin-container__actions"
      >
        <VariantForm
          :product="product"
          :constrain-width="true"
          @selected-variant="handleVariantSelected"
        >
          <AkButton
            v-if="!confirmation"
            size="lg"
            color="primary"
            symbol="cart"
            :disabled="!selectedVariant || isLoading"
            data-testid="addToCartButton"
            @click="handleAddToCart"
          >
            <LoaderIcon
              v-if="isLoading"
              :dark="true"
              :small="true"
            />
          </AkButton>
          <AkButton
            v-else
            size="lg"
            color="success"
            data-testid="confirmationButton"
          >
            <AkIcon
              size="md"
              symbol="check"
              color="white"
              class="popin-container__confirmation-icon"
            />
            {{ t('discovery.qatc.added') }}
          </AkButton>
        </VariantForm>
      </div>
      <LoaderIcon v-if="!product.refreshed" />
    </template>
  </div>
</template>

<style scoped lang="scss">
@import '@css/vue-import';
.product-block {
  @apply ds-mb-4;
}
.popin-container {
  @apply ds-py-[50px] ds-px-[35px];
  .product-snippet {
    width: 66%;
    @apply ds-p-0 ds-mx-auto;
  }
  &__actions {
    @apply ds-mt-4;
  }

  &__confirmation-icon {
    @apply ds-mr-2;
  }
}
:deep(.wholesale-price) {
  @apply ds-w-full ds-flex ds-justify-center;
}
</style>
